import SortableDxItem from '../UserDashboard/MeetingCardEditor/SortableDxItem';
import { Entry } from './ListWithNotes';
import { useDiagnosis } from '../../api/healthCoach/useDiagnosis';
import { useUserDiagnosis } from '../../api/healthCoach/useUserDiagnosis';
import { Box, Flex, useToken } from '@chakra-ui/react';
import {
  HStack,
  Text,
  VStack,
} from '@ayble-health/dashboard-components';
import { Button } from 'components/ui/button';
import { X } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { IconButton } from '../Form/IconButton';
import { UserDiagnosis } from '../../api/types';
import { PencilSVG } from '../CustomSVGs';
import { Select } from '../Form/Select';
import { SelectInstance } from 'react-select';
import { useUpdateUserDiagnoses } from '../../api/healthCoach/mutations/useUpdateUserDiagnoses';

type Props = {
  patient: string | undefined;
};

export const EditableDiagnosesList = ({ patient }: Props) => {
  const { data: availableDiagnoses } = useDiagnosis();
  const { data: userDiagnoses } = useUserDiagnosis(patient);
  const { mutation: updateUserDiagnoses } = useUpdateUserDiagnoses();

  const [isEditing, setIsEditing] = useState(false);
  const [userDiagnosesDraft, setUserDiagnosesDraft] = useState<
    UserDiagnosis[] | undefined
  >(undefined);

  const selectRef = useRef<SelectInstance | null>(null);

  const removeDxFromDraft = (diagnosisId: string) => {
    const updatedDiagnosesDraft = userDiagnosesDraft?.filter(
      (ud) => ud.id !== diagnosisId
    );
    setUserDiagnosesDraft(updatedDiagnosesDraft);
  };

  const addDxToDraft = (opt: Entry | undefined) => {
    if (!opt) return;

    const newDiagnosis = availableDiagnoses?.find((ad) => ad.id === opt.value);
    if (!newDiagnosis) return;

    // Add UserDiagnosis fields missing from Diagnosis type
    const newUserDiagnoses = {
      ...newDiagnosis,
      dx_id: opt.value,
      sort_order: null,
    };
    setUserDiagnosesDraft((currentState) => [
      ...(currentState ?? []),
      newUserDiagnoses,
    ]);
  };

  const saveNewDiagnoses = async () => {
    if (!patient || !userDiagnosesDraft) return;

    const diagnosisIds = userDiagnosesDraft?.map((ud) => ud.dx_id);

    await updateUserDiagnoses.mutateAsync({
      payload: { diagnosis_ids: diagnosisIds, user_id: patient },
    });
  };

  useEffect(() => {
    if (!userDiagnoses || userDiagnosesDraft !== undefined) return;

    setUserDiagnosesDraft(userDiagnoses);
  }, [userDiagnoses, userDiagnosesDraft]);

  const [blueGray600] = useToken('colors', ['blueGray.600']);

  if (!patient || !availableDiagnoses || !userDiagnosesDraft) return null;

  // Remove from the available options the already selected ones
  const diagnosisOptions: Entry[] = availableDiagnoses
    .filter((ad) => !userDiagnosesDraft?.some((ud) => ud.dx_id === ad.id))
    .map((ad) => ({
      label: ad.diagnosis,
      value: ad.id,
    }));

  return (
    <VStack>
      <Flex
        alignItems={isEditing ? 'center' : 'flex-start'}
        justifyContent={'space-between'}
        flexDir={'row'}
      >
        <HStack
          alignItems={'center'}
          flex={1}
          justifyContent={'space-between'}
          minH={'24px'}
          mb={2}
        >
          <HStack alignItems={'center'}>
            <Text
              color={'blueGray.600'}
              fontSize={'md'}
              fontWeight={'normal'}
              textAlign="left"
            >
              Diagnoses
            </Text>

            {!isEditing && (
              <IconButton onClick={() => setIsEditing(true)}>
                <PencilSVG />
              </IconButton>
            )}
          </HStack>

          {isEditing && (
            <IconButton
              onClick={() => {
                setIsEditing(false);
                // restore persistent state
                setUserDiagnosesDraft(userDiagnoses);
              }}
            >
              <X color={blueGray600} size={14} />
            </IconButton>
          )}
        </HStack>

        {!isEditing && (
          <HStack flex={1} pt={'4px'}>
            <Text color={'secondary.200'} fontWeight={'bold'}>
              {userDiagnosesDraft?.map((ud) => ud.diagnosis).join(', ')}
            </Text>
          </HStack>
        )}
      </Flex>

      {!!updateUserDiagnoses?.isError && (
        <Text mt={2} fontSize={'sm'} color={'error.600'}>
          An error occurred while saving, please try again later.
        </Text>
      )}

      {isEditing && (
        <>
          {userDiagnosesDraft?.map((ud, i) => (
            <SortableDxItem
              key={ud.id}
              id={ud.id}
              diagnosis={ud.diagnosis}
              index={i}
              deletable={i > 1}
              handleDelete={() => removeDxFromDraft(ud.id)}
            />
          ))}

          <Box mt={2}>
            <Select
              inputRef={selectRef as any}
              isClearable={true}
              isSearchable={true}
              onChange={addDxToDraft}
              onBlur={selectRef?.current?.clearValue() as any}
              options={diagnosisOptions}
              placeholder="Add Dx"
              value={undefined}
            />
          </Box>

          <Button
            backgroundColor={'secondary.500'}
            borderColor={'blueGray.600'}
            borderWidth={'1px'}
            h={'42px'}
            loading={updateUserDiagnoses.isLoading}
            minH={0}
            mt={6}
            onClick={saveNewDiagnoses}
            w={'100%'}
          >
            <Text color={'white'} fontSize={'md'} fontWeight={'normal'}>
              Update
            </Text>
          </Button>
        </>
      )}
    </VStack>
  );
};
